// Ubicación en el repo: src/lib/branchLogos.js
// Logo/imagen de sucursal — tabla `branches.logo_path` + bucket privado
// `branch-logos` de Supabase Storage. Las dos cosas ya existían desde la
// migración inicial (0001 columna, 0004 bucket + políticas de
// storage.objects) — ver DECISIONS.md #39, solo faltaba esta capa de la
// app. Mismo patrón que patientAttachments.js, con dos diferencias:
//
// 1. Compresión más liviana (compressImageIfNeeded con opts): un logo no
//    necesita 1920px, y si es PNG se preserva el formato para no perder
//    transparencia (a diferencia de un adjunto clínico, que siempre se
//    recomprime a JPEG).
// 2. El archivo viejo SÍ se borra al reemplazar el logo — a diferencia de
//    patient_attachments (DECISIONS.md #2, trazabilidad Ley 21.719), un
//    logo institucional no es dato clínico y no aplica esa política de
//    nunca borrar.
import { supabase } from './supabaseClient';
import { compressImageIfNeeded } from './imageCompression';

const BUCKET = 'branch-logos';
// Mismo límite fijado en el bucket (migración 0015) — se valida acá antes
// de subir para dar un mensaje claro en vez del error genérico de Storage.
const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB

function logoCompressOpts() {
  return { maxDimension: 512, jpegQuality: 0.85, skipUnderBytes: 200 * 1024, preserveType: true };
}

// Sube (o reemplaza) el logo de una sucursal y actualiza `logo_path`.
// `branchId` debe existir ya en la tabla (la ruta de Storage usa
// `{branch_id}/...`, ver policy `branch_logos_storage_write`).
export async function uploadBranchLogo(branchId, file, previousLogoPath) {
  const toUpload = await compressImageIfNeeded(file, logoCompressOpts());

  if (toUpload.size > MAX_FILE_SIZE_BYTES) {
    const mb = (toUpload.size / (1024 * 1024)).toFixed(1);
    throw new Error(`La imagen pesa ${mb} MB — el máximo permitido es 2 MB.`);
  }
  const safeName = toUpload.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
  const path = `${branchId}/${crypto.randomUUID()}-${safeName}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(path, toUpload, { contentType: toUpload.type || undefined });
  if (uploadError) throw uploadError;

  const { error: updateError } = await supabase.from('branches').update({ logo_path: path }).eq('id', branchId);
  if (updateError) {
    // No dejar el archivo huérfano en Storage si no se pudo guardar la referencia.
    await supabase.storage.from(BUCKET).remove([path]);
    throw updateError;
  }

  // Recién ahora que la fila apunta al archivo nuevo se borra el anterior
  // (si había uno) — sin dato clínico de por medio, no hace falta conservarlo.
  if (previousLogoPath) {
    await supabase.storage.from(BUCKET).remove([previousLogoPath]);
  }
  return path;
}

export async function removeBranchLogo(branchId, logoPath) {
  const { error } = await supabase.from('branches').update({ logo_path: null }).eq('id', branchId);
  if (error) throw error;
  if (logoPath) {
    await supabase.storage.from(BUCKET).remove([logoPath]);
  }
}

// URL firmada de corta duración (el bucket es privado) — usada tanto para
// la vista previa en el formulario de Sucursales como para los imprimibles.
export async function getSignedLogoUrl(logoPath) {
  if (!logoPath) return null;
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(logoPath, 60);
  if (error) throw error;
  return data.signedUrl;
}

// Atajo para audiometrías/recetas: `patient.patient_branches` ya trae
// `branches(logo_path)` embebido (ver EXAM_LIST_SELECT / PRESCRIPTION_LIST_
// SELECT). BUG encontrado (reportado por el usuario): tomar directamente
// `patient_branches[0]` fallaba para pacientes con más de una sucursal
// asociada, porque PostgREST no garantiza el orden del embed — a veces
// esa primera entrada era una sucursal sin logo, aunque el paciente sí
// estuviera también asociado a una que sí tiene. Por eso "documentos ya
// creados" (de pacientes viejos, con varias sucursales acumuladas) no
// mostraban el logo, mientras que uno recién agregado (con una sola
// sucursal) sí. Ahora se prioriza `preferredBranchId` (la sucursal activa
// en el momento de imprimir — el listado ya viene filtrado por esa
// sucursal, así que es la más relevante); si no hay coincidencia, se
// prefiere cualquier entrada que sí tenga logo antes que la primera a
// secas. Si algo falla (sin permiso, archivo borrado, etc.) se devuelve
// null en vez de romper la impresión: el logo es un detalle visual, no
// debe bloquear un documento clínico.
export async function getPatientBranchLogoUrl(patient, preferredBranchId) {
  const list = patient?.patient_branches ?? [];
  const preferred = preferredBranchId ? list.find((pb) => pb.branch_id === preferredBranchId) : null;
  const entry = preferred || list.find((pb) => pb.branches?.logo_path) || list[0];
  const logoPath = entry?.branches?.logo_path;
  if (!logoPath) return null;
  try {
    return await getSignedLogoUrl(logoPath);
  } catch {
    return null;
  }
}

// Mismo espíritu para citas/agenda, donde ya se tiene el objeto `branch`
// completo (de selectableBranches, que ahora trae logo_path).
export async function getBranchLogoUrl(branch) {
  if (!branch?.logo_path) return null;
  try {
    return await getSignedLogoUrl(branch.logo_path);
  } catch {
    return null;
  }
}
